import { getInteractionEntityType } from "../types";
import type { EntityType, InteractionEvent } from "../types";
import {
  activityDayKey,
  activityZone,
  calcTotalWaitTime,
  formatTime,
  getActivityTimeRange,
  type SegmentDayLookup,
  type SegmentZoneLookup,
  type Zone,
} from "./tripDisplay";

/**
 * The trip story: a finished trip read back as a handful of facts (the
 * recap cards) and a chapter per day (the story page). Everything here comes
 * from what was already logged: step times, posted waits, the access tier
 * and the place's type. Nothing is guessed, so a fact with no data behind it
 * is left out rather than shown as zero.
 */

/** What the recap needs from an activity. */
export interface RecapActivityLike {
  id: string;
  entityId: string | null;
  name: string | null;
  segmentId?: string | null;
  timezone?: string | null;
  events?: InteractionEvent[];
  postedWait?: number | null;
  accessTierId?: string | null;
  customResponses?: Record<string, unknown> | null;
  createdAt?: { toDate?: () => Date } | null;
  sortIndex?: number | null;
}

/** What the recap needs from a place. */
export interface RecapEntityLike {
  displayName: string;
  type: EntityType | null;
  activityOptions?: { id: string; displayName: string }[];
}

export type RecapKind = "ride" | "show" | "food" | "park" | "other";

export interface RecapActivity {
  id: string;
  name: string;
  kind: RecapKind;
  type: EntityType | undefined;
  dayKey: string | null;
  zone: Zone;
  start: Date | null;
  end: Date | null;
  /** Posted wait in minutes, when one was noted. */
  posted: number | null;
  /** Time in line in minutes, when the steps show one. */
  waited: number | null;
  /** The access tier's name ("Express", "Early Park"), when one was picked. */
  tier: string | null;
  /** Got in line and left without riding: no ride step after the queue. */
  gaveUp: boolean;
}

export interface RecapDay {
  dayKey: string;
  /** Parks and events checked into that day, in order. */
  places: string[];
  rides: number;
  shows: number;
  food: number;
  /** Minutes in line across the day's rides and shows. */
  waited: number;
  first: Date | null;
  last: Date | null;
  zone: Zone;
  /** The day's rides, shows and food in the order they happened. */
  moments: RecapActivity[];
  /** One or two sentences written from the day's activities. */
  summary: string;
}

export interface TripRecap {
  activities: RecapActivity[];
  days: RecapDay[];
  rides: number;
  shows: number;
  food: number;
  /** Distinct parks and events visited. */
  places: string[];
  /** Minutes in line across every ride and show. */
  waited: number;
  /** Rides with both a posted and a real wait: how they compare. */
  postedVsWaited: { count: number; posted: number; waited: number } | null;
  /** The same comparison per access tier, biggest first. */
  tiers: { name: string; count: number; posted: number; waited: number }[];
  /** The ride done most often, when one was done more than once. */
  topRide: { name: string; rides: RecapActivity[] } | null;
  /** The ride that beat its posted wait by the most. */
  bestWalkOn: RecapActivity | null;
  longestWait: RecapActivity | null;
  /** A line left without riding. */
  gaveUp: RecapActivity | null;
  /** The busiest day, by rides and shows. */
  bigDay: RecapDay | null;
  /** The earliest first step of any day. */
  earliest: { at: Date; zone: Zone; dayKey: string; name: string } | null;
  /** The food stop visited most, when one was visited more than once. */
  topFood: { name: string; count: number } | null;
}

const KIND_BY_TYPE: Partial<Record<EntityType, RecapKind>> = {
  ATTRACTION: "ride",
  EXPERIENCE: "show",
  RESTAURANT: "food",
  PARK: "park",
  EVENT: "park",
};

/** Steps that mean the ride or show itself happened. */
const RIDDEN_STEPS = new Set(["WAIT_END", "MAIN_EVENT_START"]);

function minutes(seconds: number): number {
  return Math.round(seconds / 60);
}

/** Read one activity the way the recap needs it. */
export function recapActivity(
  i: RecapActivityLike,
  entity: RecapEntityLike | null | undefined,
  onDay?: SegmentDayLookup,
  onZone?: SegmentZoneLookup,
): RecapActivity {
  const type = getInteractionEntityType(entity ? { type: entity.type ?? undefined } : null, i.customResponses);
  const kind: RecapKind = (type && KIND_BY_TYPE[type]) || "other";
  const events = i.events ?? [];
  const { start, end } = getActivityTimeRange(events);
  const queued = events.some((e) => e.eventType === "WAIT_START");
  const ridden = events.some((e) => RIDDEN_STEPS.has(e.eventType));
  const waitedSec = calcTotalWaitTime(events);
  const tierName = i.accessTierId
    ? entity?.activityOptions?.find((o) => o.id === i.accessTierId)?.displayName ?? null
    : null;
  return {
    id: i.id,
    name: entity?.displayName || i.name || "Activity",
    kind,
    type,
    dayKey: activityDayKey(i, onDay, onZone),
    zone: activityZone(i, onZone),
    start,
    end,
    posted: i.postedWait != null && i.postedWait > 0 ? i.postedWait : null,
    waited: waitedSec > 0 ? minutes(waitedSec) : null,
    tier: tierName,
    gaveUp: (kind === "ride" || kind === "show") && queued && !ridden && events.length > 1,
  };
}

const byStart = (a: RecapActivity, b: RecapActivity) =>
  (a.start?.getTime() ?? Number.MAX_SAFE_INTEGER) - (b.start?.getTime() ?? Number.MAX_SAFE_INTEGER);

/** Rides and shows that happened (not lines walked away from). */
const counted = (a: RecapActivity) => (a.kind === "ride" || a.kind === "show") && !a.gaveUp;

/** "A", "A and B", "A, B and C". */
export function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "45m", "2h 54m". */
export function formatMinutes(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * A day written out from its activities: the first thing done and when, what
 * came next, and anything that stood out (a line given up on, a long wait).
 * Templates only, so it never says anything the log doesn't.
 */
export function daySummary(moments: RecapActivity[]): string {
  if (moments.length === 0) return "";
  // A name done twice in a row reads "Mummy twice", not "Mummy and Mummy".
  const named: { name: string; count: number; first: RecapActivity }[] = [];
  for (const m of moments) {
    if (m.gaveUp) continue;
    const prev = named.find((n) => n.name === m.name);
    if (prev) prev.count++;
    else named.push({ name: m.name, count: 1, first: m });
  }
  const label = (n: { name: string; count: number }) =>
    n.count === 2 ? `${n.name} twice` : n.count > 2 ? `${n.name} ×${n.count}` : n.name;
  const sentences: string[] = [];
  const lead = named[0];
  if (lead) {
    const at = lead.first.start ? ` at ${formatTime(lead.first.start, lead.first.zone)}` : "";
    const next = named.slice(1, 4).map(label);
    const more = named.length - 1 - next.length;
    let s = `${label(lead)}${at}`;
    if (next.length > 0) s += more > 0 ? `, then ${next.join(", ")} and ${more} more` : `, then ${listNames(next)}`;
    sentences.push(`${s}.`);
  }
  const quit = moments.find((m) => m.gaveUp);
  if (quit) sentences.push(`Left the line for ${quit.name}.`);
  const longest = moments.reduce<RecapActivity | null>(
    (best, m) => (m.waited != null && m.waited >= 45 && (!best || m.waited > (best.waited ?? 0)) ? m : best),
    null,
  );
  if (longest) sentences.push(`Longest line: ${longest.name}, ${formatMinutes(longest.waited!)}.`);
  return sentences.join(" ");
}

/** Read a whole trip back as its recap and its days. */
export function buildTripRecap(
  items: RecapActivityLike[],
  entityOf: (entityId: string | null) => RecapEntityLike | null | undefined,
  onDay?: SegmentDayLookup,
  onZone?: SegmentZoneLookup,
): TripRecap {
  const activities = items
    .map((i) => recapActivity(i, entityOf(i.entityId), onDay, onZone))
    .sort(byStart);

  // Days, in calendar order.
  const dayMap = new Map<string, RecapActivity[]>();
  for (const a of activities) {
    if (!a.dayKey) continue;
    const list = dayMap.get(a.dayKey) ?? [];
    list.push(a);
    dayMap.set(a.dayKey, list);
  }
  const days: RecapDay[] = [...dayMap.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([dayKey, list]) => {
      const timed = list.filter((a) => a.start);
      const moments = list.filter((a) => a.kind === "ride" || a.kind === "show" || a.kind === "food");
      return {
        dayKey,
        places: [...new Set(list.filter((a) => a.kind === "park").map((a) => a.name))],
        rides: list.filter((a) => a.kind === "ride" && !a.gaveUp).length,
        shows: list.filter((a) => a.kind === "show" && !a.gaveUp).length,
        food: list.filter((a) => a.kind === "food").length,
        waited: list.filter(counted).reduce((s, a) => s + (a.waited ?? 0), 0),
        first: timed[0]?.start ?? null,
        last: timed.reduce<Date | null>((m, a) => {
          const t = a.end ?? a.start;
          return t && (!m || t > m) ? t : m;
        }, null),
        zone: timed[0]?.zone ?? list[0]?.zone,
        moments,
        summary: daySummary(moments),
      };
    });

  const rides = activities.filter((a) => a.kind === "ride" && !a.gaveUp);
  const shows = activities.filter((a) => a.kind === "show" && !a.gaveUp);
  const food = activities.filter((a) => a.kind === "food");

  // Posted against real waits, for rides with both.
  const compared = rides.filter((a) => a.posted != null && a.waited != null);
  const postedVsWaited =
    compared.length > 0
      ? {
          count: compared.length,
          posted: compared.reduce((s, a) => s + a.posted!, 0),
          waited: compared.reduce((s, a) => s + a.waited!, 0),
        }
      : null;
  const tierMap = new Map<string, { name: string; count: number; posted: number; waited: number }>();
  for (const a of compared) {
    if (!a.tier) continue;
    const t = tierMap.get(a.tier) ?? { name: a.tier, count: 0, posted: 0, waited: 0 };
    t.count++;
    t.posted += a.posted!;
    t.waited += a.waited!;
    tierMap.set(a.tier, t);
  }
  const tiers = [...tierMap.values()].sort((a, b) => b.posted - a.posted);

  // The ride done most, if any was done twice.
  const rideGroups = new Map<string, RecapActivity[]>();
  for (const r of rides) rideGroups.set(r.name, [...(rideGroups.get(r.name) ?? []), r]);
  let topRide: TripRecap["topRide"] = null;
  for (const [name, list] of rideGroups) {
    if (list.length >= 2 && (!topRide || list.length > topRide.rides.length)) topRide = { name, rides: list };
  }

  const bestWalkOn = compared.reduce<RecapActivity | null>((best, a) => {
    const saved = a.posted! - a.waited!;
    return saved > 0 && (!best || saved > best.posted! - best.waited!) ? a : best;
  }, null);
  const longestWait = [...rides, ...shows].reduce<RecapActivity | null>(
    (best, a) => (a.waited != null && (!best || a.waited > best.waited!) ? a : best),
    null,
  );
  const gaveUp = activities.find((a) => a.gaveUp) ?? null;

  const bigDay = days.reduce<RecapDay | null>(
    (best, d) => (d.rides + d.shows > 0 && (!best || d.rides + d.shows > best.rides + best.shows) ? d : best),
    null,
  );

  // The earliest start of any day, by clock time where it happened.
  let earliest: TripRecap["earliest"] = null;
  let earliestClock = Infinity;
  for (const d of days) {
    const first = activities.find((a) => a.dayKey === d.dayKey && a.start);
    if (!first?.start || !first.dayKey) continue;
    const clock = clockMinutes(first.start, first.zone);
    if (clock < earliestClock) {
      earliestClock = clock;
      earliest = { at: first.start, zone: first.zone, dayKey: first.dayKey, name: first.name };
    }
  }

  const foodGroups = new Map<string, number>();
  for (const f of food) foodGroups.set(f.name, (foodGroups.get(f.name) ?? 0) + 1);
  let topFood: TripRecap["topFood"] = null;
  for (const [name, count] of foodGroups) {
    if (count >= 2 && (!topFood || count > topFood.count)) topFood = { name, count };
  }

  return {
    activities,
    days,
    rides: rides.length,
    shows: shows.length,
    food: food.length,
    places: [...new Set(activities.filter((a) => a.kind === "park").map((a) => a.name))],
    waited: [...rides, ...shows].reduce((s, a) => s + (a.waited ?? 0), 0),
    postedVsWaited,
    tiers,
    topRide,
    bestWalkOn,
    longestWait,
    gaveUp,
    bigDay,
    earliest,
    topFood,
  };
}

/** Minutes since midnight of an instant, on the clock where it happened. */
export function clockMinutes(at: Date, zone: Zone): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hour: "numeric",
      minute: "numeric",
      hourCycle: "h23",
    }).formatToParts(at);
    const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
    const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
    return h * 60 + m;
  } catch {
    return at.getHours() * 60 + at.getMinutes();
  }
}

/** How much shorter (positive) or longer (negative) real waits were, in percent. */
export function percentUnderPosted(p: { posted: number; waited: number }): number {
  return p.posted > 0 ? Math.round((1 - p.waited / p.posted) * 100) : 0;
}
